/**
 * Modale « Rouvrir » — champ motif optionnel.
 *
 * Spec iter135 (Feb 2026) — utilisée par les fiches Tâches et Documents
 * pour confirmer la réouverture d'un élément clôturé. Le champ ``reason``
 * est OPTIONNEL : cliquer sur « Rouvrir » sans motif fonctionne comme
 * avant (le POST envoie ``{ reason: null }`` que le backend traite
 * comme motif absent). Si un motif est saisi, il est envoyé au backend
 * qui le journalise dans l'historique de l'élément (audit).
 *
 * Style aligné sur les autres modales de l'app (AviReasonModal, doc
 * reopen modal) — fond sombre, bordure #383838, header + footer avec
 * boutons Annuler / Rouvrir. Capitalisation auto (parité modules).
 */
import { useState, useRef, useEffect } from "react";
import DeskCloseX from "@/components/common/DeskCloseX";
import { RotateCcw } from "lucide-react";
import useEscapeClose from "@/hooks/useEscapeClose";
import { withFirstLetterCapitalized } from "@/lib/autoCapitalize";

export default function ReopenReasonModal({
  open = true,
  onClose,
  onConfirm,        // async (reason: string|null) => void
  busy = false,
  entityLabel = "cet élément",   // "cette tâche" | "ce document"
  headline = "Rouvrir",
  helperText = "Les rappels automatiques reprendront à partir du prochain tick.",
}) {
  const [reason, setReason] = useState("");
  const inputRef = useRef(null);
  useEscapeClose(open, onClose);

  useEffect(() => {
    if (open) {
      setReason("");
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    if (busy) return;
    const clean = reason.trim();
    await onConfirm(clean || null);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-6"
      style={{ background: "rgba(0,0,0,0.55)" }}
      data-testid="reopen-reason-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}
    >
      <div
        className="w-full max-w-[520px] rounded-lg overflow-hidden"
        style={{ background: "#1B1B1B", border: "1px solid #383838" }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{ borderBottom: "1px solid #2A2A2A" }}
        >
          <div className="inline-flex items-center gap-2">
            <span
              className="inline-flex items-center justify-center rounded-full"
              style={{
                width: 26, height: 26,
                background: "rgba(139,92,246,0.18)",
                color: "var(--status-cloture-docs)",
              }}
              aria-hidden="true"
            >
              <RotateCcw size={14} strokeWidth={2.4} />
            </span>
            <div className="flex flex-col leading-tight">
              <span className="text-[13.5px] font-semibold" style={{ color: "#EDEDED" }}>
                {headline}
              </span>
              <span className="text-[11px]" style={{ color: "#9CA3AF" }}>
                Réouverture de {entityLabel}
              </span>
            </div>
          </div>
          <DeskCloseX onClick={onClose} testId="reopen-reason-close" />
        </div>

        {/* Body */}
        <div className="p-4 space-y-3">
          <label className="block">
            <span className="text-[11.5px] uppercase tracking-wide font-semibold" style={{ color: "#9CA3AF" }}>
              Motif de réouverture <span className="normal-case font-normal" style={{ color: "#6B7280" }}>(optionnel)</span>
            </span>
            <textarea
              ref={inputRef}
              value={reason}
              onChange={withFirstLetterCapitalized((e) => setReason(e.target.value))}
              rows={4}
              maxLength={500}
              placeholder="Ex. : nouvelle information reçue, correction demandée, etc."
              className="w-full mt-1.5 p-2.5 rounded-md text-[13px] resize-none placeholder:text-[#666]"
              style={{ background: "#212121", border: "1px solid #383838", color: "#EDEDED" }}
              data-testid="reopen-reason-input"
            />
          </label>
          <p className="text-[11.5px]" style={{ color: "#9CA3AF" }}>
            {helperText}
          </p>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-2 px-4 py-3"
          style={{ borderTop: "1px solid #2A2A2A", background: "#181818" }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="h-9 px-3.5 rounded-md text-[12.5px] font-semibold"
            style={{
              background: "transparent",
              border: "1px solid #383838",
              color: "#D1D5DB",
              cursor: busy ? "not-allowed" : "pointer",
            }}
            data-testid="reopen-reason-cancel"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md text-[12.5px] font-semibold text-white transition-all hover:brightness-110"
            style={{
              background: "var(--status-cloture-docs)",
              boxShadow: busy ? "none" : "0 4px 12px rgba(139,92,246,0.35)",
              cursor: busy ? "wait" : "pointer",
            }}
            data-testid="reopen-reason-confirm"
          >
            <RotateCcw size={13} strokeWidth={2.4} />
            {busy ? "…" : "Rouvrir"}
          </button>
        </div>
      </div>
    </div>
  );
}
